import get from "lodash/get";
import i18n from "../i18n";
import { whoIsAthleteCoach } from "../utils/user-helpers";

/**
 * Ensure logged in / loaded activeUser is a coach.
 * And if trying to access athlete-portal, that the right athlete is passed in the query
 * Also loads athlete object from the coach on locals as locals.athlete
 */
const EnsureCoach = async ({ req, res, load, locals, login, flash }, next) => {
  // Require that the user details were loaded
  // If not coach then redirect to user's calendar.
  if (get(locals, "activeUser.permissions", []).indexOf("coach") < 0) {
    return res.redirect("/dashboard/calendar");
  }

  if (!locals.activeUser.athletes) {
    locals.activeUser.athletes = [];
  }

  if (req.path.includes("/athlete-portal")) {
    // if changing athlete while in /programs

    // if no athlete is passed in the query then no entry.
    // if passed athlete does not belong to coach then no entry.
    await load("athlete", { user: locals.query.athlete });

    // Any coach in the organization can open any of its athletes, but the
    // athlete only lands on `activeUser.athletes` for their own coach. When a
    // link lost ?organizationCoach (e.g. a back button), derive it from the
    // athlete's actual coach so it isn't mistaken for an inaccessible athlete.
    const athleteCoach = get(locals, "athlete.coach");
    const activeOrganizationId = get(locals, "activeUser.organizationId");
    if (
      !locals.query.organizationCoach &&
      activeOrganizationId &&
      athleteCoach?._id &&
      String(athleteCoach._id) !== String(locals.activeUser._id) &&
      String(athleteCoach.organizationId) === String(activeOrganizationId)
    ) {
      locals.query.organizationCoach = String(athleteCoach._id);
    }

    if (locals.query.organizationCoach) {
      await load("coachOrganizationInformation", {
        user: locals.query.organizationCoach,
      });
    }
    // check de athletes for the coach in session or another coaches from the organization
    // (here we have to join the coach in session and also the athletes from the another coach, unless
    //  the id is the same as the coachOrganizationInformation)
    const athletesToRequest =
      locals.coachOrganizationInformation &&
      locals.coachOrganizationInformation._id.toString() !==
        locals.activeUser._id.toString()
        ? [
            ...get(locals, `activeUser.athletes`, []),
            ...get(locals, `coachOrganizationInformation.athletes`, []),
          ]
        : get(locals, `activeUser.athletes`, []);

    const coachAthlete = athletesToRequest.find(
      (athlete) => String(athlete._id) === get(locals, "query.athlete")
    );
    if (!locals.athlete || !coachAthlete) {
      flash("error", i18n.t("athlete-portal:flexible.errors.noAthleteAccess"));
      return res.redirect("/coaching-portal/athletes");
    }

    locals.athlete = {
      ...coachAthlete,
      ...locals.athlete,
    };

    // `coachAthlete.annualPlan` above is the coach's embedded legacy field -
    // it goes stale the moment msb-api's periodization storage switch is on,
    // since writes stop touching it. Overridden here with the switch-aware
    // read, so every consumer of `locals.athlete.annualPlan` (AnnualPlanner.js
    // and its children, dateRange.js, planStatus.js, phase-templates-modal.js)
    // sees the same data regardless of which store answered.
    const organizationCoach = locals.query.organizationCoach || undefined;
    const organizationCoachId = organizationCoach
      ? whoIsAthleteCoach(
          locals.activeUser.athletes,
          coachAthlete._id,
          locals.activeUser._id,
          organizationCoach
        )
      : undefined;

    const periodizationData = await load("periodizationData", {
      athleteId: coachAthlete._id,
      organizationCoachId,
    });
    locals.athlete.annualPlan = periodizationData;

    if (locals.athlete.status?.inactiveReason === "Inactive subscription") {
      fullAthlete.status = {
        inactive: false,
        inactiveReason: "",
      };
    }
    // if inactive, and trying to access calendar or metrics, redirect to profile
    if (locals.athlete.status.inactive && /(calendar)|(metrics)/.test(req.path))
      return res.redirect(
        `/athlete-portal/profile/?athlete=${get(locals, "query.athlete")}`
      );

    // if trying to switch athlete from program editor, do not allow it. See MSB-908 - https://tillerdigital.atlassian.net/browse/MSB-908
    if (
      locals.query.athleteSwitch &&
      (/(athlete-portal\/programs\/edit-program)/.test(req.path) ||
        /(athlete-portal\/programs\/program-overview)/.test(req.path) ||
        /(athlete-portal\/programs\/workout)/.test(req.path) ||
        /(athlete-portal\/metrics\/custom)/.test(req.path))
    ) {
      const newPath = req.path
        .replace(/\b\/edit-program\/\b.*\b\?\b/, "?")
        .replace(/\b\/program-overview\/\b.*\b\?\b/, "?")
        .replace(/\b\/workout\/\b.*\b\?\b/, "?")
        .replace(/\b\/metrics\b.*\b\?\b/, "/metrics?")
        .replace(/\bathleteSwitch\b.*\btrue\b/, "");
      return res.redirect(newPath);
    }
  }

  return next();
};

export default EnsureCoach;
